import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getPickupList, cancelPickup, PICKUP_STATUSES } from '@/lib/pickups-api';
import type { PickupRecord } from '@/lib/pickups-types';
import { statusPill } from '@/lib/pickups-display';
import { fmtCdxDate, fmtCdxDateTime } from '@/lib/dates';
import { cn } from '@/lib/cn';

/**
 * Pickups - the tracking / queue view. Lists PickupRecord sidecars (newest first) with a status
 * filter; admins can cancel a scheduled pickup inline. Read-only otherwise.
 */

interface PickupsProps {
  isAdmin: boolean;
}

const PAGE_LIMIT = 50;

export function Pickups({ isAdmin }: PickupsProps) {
  const [rows, setRows] = useState<PickupRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function load(reset: boolean, cursor?: string | undefined) {
    if (reset) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await getPickupList({ status: status || undefined, limit: PAGE_LIMIT, cursor });
      setNextCursor(res.nextCursor);
      setRows((prev) => (reset ? res.items : [...prev, ...res.items]));
    } catch (e) {
      const code = (e as { status?: number }).status;
      setError(`Could not load pickups${code ? ` (HTTP ${code})` : ''}.`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    void load(true, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function onCancel(rec: PickupRecord) {
    if (!rec.confirmationNumber) return;
    const key = `${rec.memberId}:${rec.confirmationNumber}`;
    setBusyKey(key);
    setActionMsg(null);
    try {
      const res = await cancelPickup({
        memberId: rec.memberId,
        confirmationNumber: rec.confirmationNumber,
      });
      if (res.ok) {
        setActionMsg({ kind: 'ok', text: `Canceled pickup for member ${rec.memberId}.` });
      } else {
        const err = (res.data as { error?: string }).error ?? `HTTP ${res.status}`;
        setActionMsg({ kind: 'err', text: err });
      }
      await load(true, undefined);
    } catch (e) {
      setActionMsg({ kind: 'err', text: `Cancel failed: ${String((e as Error).message)}` });
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">CDX Pickups · Queue</div>
        <h1 className="section-title">
          Pickups <em>Tracking</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Every scheduled, canceled, and failed carrier pickup, newest first. Filter by status;
          admins can cancel a scheduled pickup.
        </p>
      </header>

      {/* ── Filter ────────────────────────────────────────────────── */}
      <section>
        <div className="card">
          <div className="field" style={{ marginBottom: 0, maxWidth: 240 }}>
            <label className="field-label" htmlFor="pk-status">Status</label>
            <select
              id="pk-status"
              className="select-input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {PICKUP_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusPill(s).label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {actionMsg && (
        <section>
          <div className={cn('note', actionMsg.kind === 'err' ? 'danger' : '')}>
            <b>{actionMsg.kind === 'err' ? 'Failed.' : 'Done.'}</b> {actionMsg.text}
          </div>
        </section>
      )}

      {/* ── Table ─────────────────────────────────────────────────── */}
      <section>
        <div className="block-head">
          Pickup Records
          <span className="aux">{rows.length} loaded</span>
        </div>

        {loading ? (
          <div className="card">
            <LoadingSpinner label="Loading pickups…" />
          </div>
        ) : error ? (
          <div className="note danger">
            <b>Error.</b> {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="note">No pickups match the current filter.</div>
        ) : (
          <>
            <div className="card card-flush">
              <table className="dt">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Status</th>
                    <th>Pickup Date</th>
                    <th>Confirmation</th>
                    <th>Scheduled By</th>
                    <th>Scheduled At</th>
                    {isAdmin && <th />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const key = `${r.memberId}:${r.confirmationNumber ?? 'none'}:${r.scheduledAt}`;
                    const sp = statusPill(r.status);
                    return (
                      <tr key={key}>
                        <td>
                          <div className="mono">{r.memberId}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-400)' }}>
                            {r.name.first} {r.name.last}
                          </div>
                        </td>
                        <td>
                          <span className={cn('pill', sp.cls)}>{sp.label}</span>
                        </td>
                        <td className="mono">{fmtCdxDate(r.pickupDate) || '--'}</td>
                        <td className="mono">{r.confirmationNumber ?? '--'}</td>
                        <td>{r.scheduledByEmail || '--'}</td>
                        <td className="mono">{fmtCdxDateTime(r.scheduledAt) || '--'}</td>
                        {isAdmin && (
                          <td style={{ textAlign: 'right' }}>
                            {r.status === 'scheduled' && r.confirmationNumber && (
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => onCancel(r)}
                                disabled={busyKey === `${r.memberId}:${r.confirmationNumber}`}
                              >
                                {busyKey === `${r.memberId}:${r.confirmationNumber}` ? '…' : 'Cancel'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {nextCursor && (
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => void load(false, nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
